import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Alert,
  Assignment,
  AssignmentSubmission,
  Attendance,
  Class,
  Notification,
  Quiz,
  QuizAttempt,
  Student,
} from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

/**
 * Student Dashboard Controller
 * Provides comprehensive dashboard data for student mobile app
 */
class StudentDashboardController {
  /**
   * Get complete student dashboard data
   * @route GET /api/v1/student/dashboard
   * @access Private (Student only)
   */
  async getDashboard(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Get student profile
      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).populate("userId", "email isNotificationEnabled");

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Fetch all dashboard data in parallel for better performance
      const [
        upcomingQuizzes,
        pendingAssignments,
        recentResults,
        progressReport,
        notifications,
        attendanceStats,
        activeAlerts,
      ] = await Promise.all([
        this.getUpcomingQuizzes(student._id, student.schoolId),
        this.getPendingAssignments(student._id, student.schoolId),
        this.getRecentResults(student._id, student.schoolId),
        this.getProgressReport(student._id, student.schoolId),
        this.getRecentNotifications(new mongoose.Types.ObjectId(userId)),
        this.getAttendanceStats(student._id, student.schoolId),
        this.getActiveAlerts(student._id),
      ]);

      // Get student's enrolled classes count
      const enrolledClasses = await Class.countDocuments({
        schoolId: student.schoolId,
        studentIds: student._id,
        isActive: true,
      });

      res.status(200).json({
        success: true,
        message: "Dashboard data retrieved successfully",
        data: {
          student: {
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
            email: (student.userId as any)?.email,
            profilePicture: student.profilePicture,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            gpa: student.academicRecord?.averageGrade,
          },
          overview: {
            enrolledClasses,
            pendingAssignments: pendingAssignments.length,
            upcomingQuizzes: upcomingQuizzes.length,
            unreadNotifications: notifications.filter(n => !n.isRead).length,
            activeAlerts: activeAlerts.length,
            attendanceRate: attendanceStats.attendanceRate,
          },
          upcomingQuizzes,
          pendingAssignments,
          recentResults,
          progressReport,
          notifications: notifications.slice(0, 5), // Latest 5 notifications
          attendanceStats,
          activeAlerts,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get student dashboard error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve dashboard data",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get progress report data (monthly academic performance)
   * @route GET /api/v1/student/progress-report
   * @access Private (Student only)
   */
  async getProgressReport(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
  ): Promise<any> {
    try {
      // Get current date and calculate 8 months back
      const now = new Date();
      const eightMonthsAgo = new Date();
      eightMonthsAgo.setMonth(now.getMonth() - 8);

      // Get all graded assignments and quizzes from last 8 months
      const [assignments, quizzes] = await Promise.all([
        AssignmentSubmission.find({
          studentId,
          status: "graded",
          gradedAt: { $gte: eightMonthsAgo },
        })
          .populate("assignment", "totalMarks")
          .sort({ gradedAt: 1 }),
        QuizAttempt.find({
          studentId,
          status: "graded",
          submittedAt: { $gte: eightMonthsAgo },
        })
          .populate("quiz", "totalMarks")
          .sort({ submittedAt: 1 }),
      ]);

      // Group by month and calculate average
      const monthlyData: Record<string, { total: number; count: number }> = {};

      // Process assignments
      for (const submission of assignments) {
        if (submission.gradedAt && submission.marksObtained !== undefined) {
          const month = submission.gradedAt.toLocaleString("en-US", {
            month: "short",
          });
          const year = submission.gradedAt.getFullYear();
          const key = `${month} ${year}`;

          if (!monthlyData[key]) {
            monthlyData[key] = { total: 0, count: 0 };
          }

          const percentage =
            ((submission.marksObtained || 0) /
              ((submission as any).assignment?.totalMarks || 100)) *
            100;
          monthlyData[key].total += percentage;
          monthlyData[key].count += 1;
        }
      }

      // Process quizzes
      for (const attempt of quizzes) {
        if (attempt.submittedAt && attempt.totalMarksObtained !== undefined) {
          const month = attempt.submittedAt.toLocaleString("en-US", {
            month: "short",
          });
          const year = attempt.submittedAt.getFullYear();
          const key = `${month} ${year}`;

          if (!monthlyData[key]) {
            monthlyData[key] = { total: 0, count: 0 };
          }

          const percentage =
            ((attempt.totalMarksObtained || 0) /
              ((attempt as any).quiz?.totalMarks || 100)) *
            100;
          monthlyData[key].total += percentage;
          monthlyData[key].count += 1;
        }
      }

      // Generate last 8 months labels
      const months: string[] = [];
      const data: number[] = [];

      for (let i = 7; i >= 0; i--) {
        const date = new Date();
        date.setMonth(now.getMonth() - i);
        const monthLabel = date.toLocaleString("en-US", { month: "short" });
        const year = date.getFullYear();
        const key = `${monthLabel} ${year}`;

        months.push(monthLabel);

        if (monthlyData[key] && monthlyData[key].count > 0) {
          const average = monthlyData[key].total / monthlyData[key].count;
          data.push(Math.round(average));
        } else {
          data.push(0);
        }
      }

      // Calculate current month average
      const currentMonth = now.toLocaleString("en-US", { month: "short" });
      const currentYear = now.getFullYear();
      const currentKey = `${currentMonth} ${currentYear}`;
      const currentAverage = monthlyData[currentKey]
        ? Math.round(
            monthlyData[currentKey].total / monthlyData[currentKey].count,
          )
        : 0;

      const lastValue = data.at(-1) ?? 0;
      const secondLastValue = data.at(-2) ?? 0;
      let trend = "stable";
      if (data.length >= 2) {
        if (lastValue > secondLastValue) {
          trend = "up";
        } else if (lastValue < secondLastValue) {
          trend = "down";
        }
      }

      return {
        months,
        data,
        currentMonth: currentMonth,
        currentAverage,
        trend,
      };
    } catch (error) {
      logger.error("Get progress report error:", error);
      return {
        months: [],
        data: [],
        currentMonth: new Date().toLocaleString("en-US", { month: "short" }),
        currentAverage: 0,
        trend: "stable",
      };
    }
  }

  /**
   * Get upcoming quizzes
   */
  private async getUpcomingQuizzes(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
  ): Promise<any[]> {
    try {
      // Get student's classes
      const studentClasses = await Class.find({
        schoolId,
        studentIds: studentId,
        isActive: true,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      // Get upcoming quizzes (published, not yet deadline passed)
      const quizzes = await Quiz.find({
        classId: { $in: classIds },
        status: "published",
        deadline: { $gte: new Date() },
      })
        .populate("class", "className classCode")
        .sort({ deadline: 1 })
        .limit(5);

      return quizzes.map(quiz => ({
        id: quiz.id,
        title: quiz.title,
        className: (quiz as any).class?.className,
        deadline: quiz.deadline,
        dueTime: this.formatDueTime(quiz.deadline),
        duration: quiz.duration,
        totalMarks: quiz.totalMarks,
        questionCount: quiz.questions?.length || 0,
      }));
    } catch (error) {
      logger.error("Get upcoming quizzes error:", error);
      return [];
    }
  }

  /**
   * Get pending assignments
   */
  private async getPendingAssignments(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
  ): Promise<any[]> {
    try {
      // Get student's classes
      const studentClasses = await Class.find({
        schoolId,
        studentIds: studentId,
        isActive: true,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      // Get published assignments
      const assignments = await Assignment.find({
        classId: { $in: classIds },
        status: "published",
      })
        .populate("class", "className classCode")
        .sort({ deadline: 1 });

      // Check submission status for each assignment
      const pendingAssignments = await Promise.all(
        assignments.map(async assignment => {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId,
          });

          // Only include if not submitted or submitted but not graded
          if (!submission || submission.status === "submitted") {
            return {
              id: assignment.id,
              title: assignment.title,
              className: (assignment as any).class?.className,
              deadline: assignment.deadline,
              dueDate: this.formatDueDate(assignment.deadline),
              isOverdue: new Date() > assignment.deadline,
              totalMarks: assignment.totalMarks,
              status: submission ? "submitted" : "pending",
              submittedAt: submission?.submittedAt,
            };
          }
          return null;
        }),
      );

      return pendingAssignments.filter(Boolean).slice(0, 10);
    } catch (error) {
      logger.error("Get pending assignments error:", error);
      return [];
    }
  }

  /**
   * Get recent results (graded assignments and quizzes)
   */
  private async getRecentResults(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
  ): Promise<any[]> {
    try {
      // Get recent graded assignments
      const assignments = await AssignmentSubmission.find({
        studentId,
        status: "graded",
      })
        .populate({
          path: "assignment",
          select: "title totalMarks class",
          populate: {
            path: "class",
            select: "className classCode",
          },
        })
        .sort({ gradedAt: -1 })
        .limit(5);

      // Get recent graded quizzes
      const quizzes = await QuizAttempt.find({
        studentId,
        status: "graded",
      })
        .populate({
          path: "quiz",
          select: "title totalMarks class",
          populate: {
            path: "class",
            select: "className classCode",
          },
        })
        .sort({ submittedAt: -1 })
        .limit(5);

      // Combine and format results
      const results: any[] = [];

      for (const submission of assignments) {
        const assignment = (submission as any).assignment;
        results.push({
          id: submission.id,
          type: "assignment",
          title: assignment?.title || "Assignment",
          className: assignment?.class?.className || "Unknown Class",
          marksObtained: submission.marksObtained || 0,
          totalMarks: assignment?.totalMarks || 100,
          percentage: Math.round(
            ((submission.marksObtained || 0) /
              (assignment?.totalMarks || 100)) *
              100,
          ),
          gradedAt: submission.gradedAt,
          date: this.formatDate(submission.gradedAt),
        });
      }

      for (const attempt of quizzes) {
        const quiz = (attempt as any).quiz;
        results.push({
          id: attempt.id,
          type: "quiz",
          title: quiz?.title || "Quiz",
          className: quiz?.class?.className || "Unknown Class",
          marksObtained: attempt.totalMarksObtained || 0,
          totalMarks: quiz?.totalMarks || 100,
          percentage: Math.round(
            ((attempt.totalMarksObtained || 0) / (quiz?.totalMarks || 100)) *
              100,
          ),
          gradedAt: attempt.submittedAt,
          date: this.formatDate(attempt.submittedAt),
        });
      }

      // Sort by date and return top 5
      return results
        .sort((a, b) => {
          const dateA = a.gradedAt ? new Date(a.gradedAt).getTime() : 0;
          const dateB = b.gradedAt ? new Date(b.gradedAt).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 5);
    } catch (error) {
      logger.error("Get recent results error:", error);
      return [];
    }
  }

  /**
   * Get recent notifications
   */
  private async getRecentNotifications(
    userId: mongoose.Types.ObjectId,
  ): Promise<any[]> {
    try {
      const notifications = await Notification.find({
        userId,
      })
        .sort({ createdAt: -1 })
        .limit(10);

      return notifications.map(notification => ({
        id: notification.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        priority: notification.priority,
        isRead: notification.isRead,
        actionUrl: notification.actionUrl,
        createdAt: notification.createdAt,
        relativeTime: this.getRelativeTime(notification.createdAt),
      }));
    } catch (error) {
      logger.error("Get recent notifications error:", error);
      return [];
    }
  }

  /**
   * Get attendance statistics
   */
  private async getAttendanceStats(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
  ): Promise<any> {
    try {
      // Get attendance records for current semester
      const attendanceRecords = await Attendance.find({
        studentId,
        schoolId,
      });

      const total = attendanceRecords.length;
      const present = attendanceRecords.filter(
        a => a.status === "present",
      ).length;
      const late = attendanceRecords.filter(a => a.status === "late").length;
      const absent = attendanceRecords.filter(
        a => a.status === "absent",
      ).length;

      const attendanceRate =
        total > 0 ? Math.round((present / total) * 100) : 0;

      return {
        total,
        present,
        late,
        absent,
        attendanceRate,
      };
    } catch (error) {
      logger.error("Get attendance stats error:", error);
      return {
        total: 0,
        present: 0,
        late: 0,
        absent: 0,
        attendanceRate: 0,
      };
    }
  }

  /**
   * Get active alerts
   */
  private async getActiveAlerts(
    studentId: mongoose.Types.ObjectId,
  ): Promise<any[]> {
    try {
      const alerts = await Alert.find({
        studentId,
        status: { $in: ["pending", "acknowledged", "in_progress"] },
      }).sort({ requestedAt: -1 });

      return alerts.map(alert => ({
        id: alert.id,
        type: alert.type,
        title: alert.title,
        status: alert.status,
        priority: alert.priority,
        requestedAt: alert.requestedAt,
      }));
    } catch (error) {
      logger.error("Get active alerts error:", error);
      return [];
    }
  }

  /**
   * Helper: Format due time (e.g., "11:59 pm")
   */
  private formatDueTime(date: Date): string {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  /**
   * Helper: Format due date (e.g., "27 November 2024")
   */
  private formatDueDate(date: Date): string {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  /**
   * Helper: Format date (e.g., "27 November 2024")
   */
  private formatDate(date?: Date): string {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  /**
   * Helper: Get relative time (e.g., "2 hours ago")
   */
  private getRelativeTime(date: Date): string {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
    if (diffHours < 24)
      return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return past.toLocaleDateString();
  }
}

export const studentDashboardController = new StudentDashboardController();
